package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import com.swetdot.swetgram.domain.GroupVisibility
import com.swetdot.swetgram.protocol.Constraints
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetPressFeedback
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetTextField
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.design.swetAppear
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * The create-group form -- SPS §12.2.
 *
 * THE SCREEN IS ARRANGED AROUND ONE FACT: `visibility` is chosen here or never. The contract's
 * `UpdateGroupRequest` offers name, description, username and avatar and deliberately not
 * visibility (DECISIONS §7, recorded in pass 1), so every other box on this form is a decision the
 * user can revisit and exactly one is not. The layout says so rather than leaving it to be found
 * out: the two options are full-width cards with a sentence each about what they mean, neither is
 * pre-selected, and the line above them states the permanence before the choice is made rather
 * than under it as a footnote.
 *
 * WHY NOTHING IS PRE-SELECTED. A default would be the app making the one irreversible decision on
 * behalf of somebody who did not read the section, and there is no safe side to default to:
 * private cannot become public and public cannot become private, so whichever way it fell,
 * somebody would end up permanently on the wrong one. The Create button stays live -- pressing it
 * with nothing chosen is what surfaces the sentence -- because a disabled button explains nothing.
 *
 * THE FORM IS VALIDATED BEFORE IT IS SENT, and not merely to save a wait. DECISIONS §12 makes
 * group creation 5/day/user and consumes the unit "before the body is read": a refused request
 * still costs a fifth of the day. Verified against the deployed server, which answered `429` to a
 * body it never validated. `validateCreateGroup` says WHICH rule was broken -- never "invalid".
 *
 * The screen is stateless. `CreateGroupViewModel` holds the form so that every decision on it is a
 * function of its inputs and testable without a renderer, which is the arrangement `NewChatScreen`
 * already uses and `ChatViewModelTest` argues for.
 *
 * NAVIGATION ON SUCCESS is the caller's: watch `state.created`, route, then call
 * `CreateGroupViewModel.consumeCreated` so a recomposition cannot push the same group twice.
 */
@Composable
fun CreateGroupScreen(
    state: CreateGroupUiState,
    onFormChange: (CreateGroupForm) -> Unit,
    onSubmit: () -> Unit,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    onDismissBanner: (() -> Unit)? = null,
) {
    val colors = SwetgramThemeTokens.colors
    val form = state.form
    val errors = state.errors
    // While a request is in flight every control is inert: the button because a second tap is a
    // second group (there is no idempotency key on `createGroup`), and the fields because editing
    // them would describe a body that has already gone.
    val editable = !state.submitting

    Column(modifier.fillMaxSize()) {
        // §11.7: a direct title and a back arrow.
        // TODO(localization): every user-facing string in this file moves into resources (EN + RU).
        SwetTopAppBar(title = "New group", onBack = onBack)

        // Above the form, never in place of it. Whatever the user typed is still on screen and
        // still theirs to correct.
        val banner = state.banner
        if (banner != null) {
            SwetErrorBanner(message = banner, onDismiss = onDismissBanner)
        }

        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                // Both insets go before `verticalScroll`, so they inset the viewport rather than
                // travelling with the content. A pushed route has no tab bar under it to carry
                // the navigation-bar inset, and the Create button would otherwise sit on the
                // system buttons.
                .imePadding()
                .navigationBarsPadding()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = Space.screenMargin, vertical = Space.three),
        ) {
            // ------------------------------------------------------------------ name
            SwetTextField(
                value = form.name,
                onValueChange = { onFormChange(form.copy(name = it)) },
                // §11.2 asks for the label as well as the placeholder: the placeholder is gone the
                // moment there is a value, and a filled box with no label is a rounded rectangle
                // with a word in it.
                label = "Group name",
                placeholder = "What is it called?",
                enabled = editable,
                keyboardOptions = KeyboardOptions(
                    capitalization = KeyboardCapitalization.Words,
                    imeAction = ImeAction.Next,
                ),
                errorText = fieldMessage(state, FIELD_NAME, errors.name),
            )

            Spacer(Modifier.height(Space.two))

            // ------------------------------------------------------------------ description
            SwetTextField(
                value = form.description,
                onValueChange = { onFormChange(form.copy(description = it)) },
                label = "Description",
                // §11.2's placeholder tone: say what to type. "Optional" is carried by the note
                // under the field rather than by the placeholder, which disappears.
                placeholder = "What is the group for?",
                enabled = editable,
                // Prose, like the bio on Edit Profile. A single line would hide most of the 500
                // characters the contract allows.
                singleLine = false,
                keyboardOptions = KeyboardOptions(
                    capitalization = KeyboardCapitalization.Sentences,
                    imeAction = ImeAction.Default,
                ),
                errorText = fieldMessage(state, FIELD_DESCRIPTION, errors.description),
            )

            // A counter rather than a hard cap on input: silently swallowing the tail of a pasted
            // paragraph looks like a broken keyboard. The limit is stated here and enforced on
            // Create. Same treatment the bio field on Edit Profile gets.
            val descriptionLength = form.description.trim().length
            Text(
                text = "Optional$SEPARATOR$descriptionLength/$GROUP_DESCRIPTION_MAX",
                style = MaterialTheme.typography.labelSmall,
                color = if (descriptionLength > GROUP_DESCRIPTION_MAX) {
                    colors.danger
                } else {
                    // Secondary, not tertiary: §22.1 keeps `text.tertiary` for placeholder and
                    // disabled text only -- it measures 2.31:1 on a light card.
                    colors.textSecondary
                },
                textAlign = TextAlign.End,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = Space.one),
            )

            Spacer(Modifier.height(Space.three))

            // ------------------------------------------------------------------ visibility
            VisibilitySection(
                selected = form.visibility,
                enabled = editable,
                onSelect = { choice -> onFormChange(form.copy(visibility = choice)) },
                errorText = fieldMessage(state, FIELD_VISIBILITY, errors.visibility),
            )

            // ------------------------------------------------------------------ username
            //
            // Public groups only, and the box is not merely disabled for a private one -- it is
            // absent. The contract does not permit a private group to carry a username at all
            // ("unavailable for private groups"), so a greyed-out field would be offering
            // something that does not exist for this kind of group rather than something that is
            // temporarily out of reach.
            //
            // A plain `if` with `swetAppear` rather than `AnimatedVisibility`: the node is gone on
            // the frame it stops being relevant, and `swetAppear` is exactly the §19.2 `motion.fast`
            // fade written for content a screen swaps in this way. It collapses to an instant swap
            // under reduced motion (§22.6).
            if (form.visibility == GroupVisibility.PUBLIC) {
                Column(Modifier.swetAppear()) {
                    Spacer(Modifier.height(Space.three))
                    SwetTextField(
                        value = form.username,
                        onValueChange = { onFormChange(form.copy(username = it)) },
                        label = "Group username",
                        placeholder = "book_club",
                        enabled = editable,
                        keyboardOptions = KeyboardOptions(
                            // The contract's usernames are lowercase-only, so autocapitalising
                            // here would have the keyboard fight the rule the field is about.
                            capitalization = KeyboardCapitalization.None,
                            imeAction = ImeAction.Done,
                        ),
                        errorText = fieldMessage(state, FIELD_USERNAME, errors.username),
                    )
                    FieldNote(
                        // What the handle is FOR, and -- the useful half -- that it is not the
                        // permanent thing on this screen. DECISIONS §7: group username changes
                        // follow the same 30-day rule as a user's.
                        //
                        // No example URL. `public_url` is derived by the server from an origin the
                        // client does not own, and printing a host here is a promise about
                        // somebody else's configuration.
                        text = "This is how people find the group. You can change it later, " +
                            "once every 30 days.",
                    )
                }
            }

            Spacer(Modifier.height(Space.four))

            SwetPrimaryButton(
                text = "Create group",
                onClick = onSubmit,
                // Live even when the form is incomplete: pressing it is how the problems become
                // visible. Only an in-flight request takes it away, and that is §25.1's rule about
                // loading preventing duplicate actions -- which here means preventing a second
                // group and a second unit of a five-a-day budget.
                enabled = state.canSubmit,
                loading = state.submitting,
            )

            Spacer(Modifier.height(Space.three))
        }
    }
}

/** Between two parts of one small line. U+00B7, with hair space either side. */
private const val SEPARATOR = " · "

// ---------------------------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------------------------

/**
 * THE SDS IS SILENT ON WHAT THIS CONTROL LOOKS LIKE. §11's component system defines buttons, text
 * fields, a search bar, cards, avatars, the two bars and the FABs -- and no selection control of
 * any kind: no radio, no segmented control, no switch. So the FORM of this is a DECISION and not
 * spec, built from §11.4's card (which §11.4 lists for "Settings sections") plus Selected and
 * Unselected from §25's own list of the states a component owes.
 *
 * Two full-width cards rather than a segmented control, because each option needs a sentence
 * explaining what it commits to, and a two-segment control has room for one word each. Somebody
 * choosing between "Public" and "Private" on the strength of the words alone is exactly the casual
 * pick this screen exists to prevent.
 */
@Composable
private fun VisibilitySection(
    /**
     * `domain.GroupVisibility?`, not `domain.GroupVisibilityChoice`. The choice type carries a
     * public group's username inside it, which is right for a request and wrong for a control: this
     * one has to be able to say "nothing picked yet", and the username lives in its own box. The
     * two are joined by `CreateGroupForm.toNewGroup`.
     */
    selected: GroupVisibility?,
    enabled: Boolean,
    onSelect: (GroupVisibility) -> Unit,
    errorText: String?,
) {
    val colors = SwetgramThemeTokens.colors

    Text(
        text = "Who can join",
        // §5.3's `label.medium`, the slot the app's other section headings use.
        style = MaterialTheme.typography.labelMedium,
        color = colors.textSecondary,
    )
    Spacer(Modifier.height(Space.half))
    Text(
        // Said BEFORE the options, not under them. This is the sentence the whole screen is
        // arranged around, and a note beneath a control is read after the choice has been made.
        text = "This is the one thing you cannot change afterwards. The name, the description " +
            "and the picture can all be edited later.",
        style = MaterialTheme.typography.bodySmall,
        color = colors.textSecondary,
    )

    Spacer(Modifier.height(Space.two))

    VisibilityOption(
        title = "Private",
        // DECISIONS §7: private groups use an invite link and administrator-approved join
        // requests, and only Owner or Admin may share the invitation.
        body = "People join with an invite link, and an owner or admin approves each request.",
        isSelected = selected == GroupVisibility.PRIVATE,
        enabled = enabled,
        onSelect = { onSelect(GroupVisibility.PRIVATE) },
    )

    Spacer(Modifier.height(Space.one))

    VisibilityOption(
        title = "Public",
        // DECISIONS §7: public groups have a unique username and a direct Join after a preview,
        // and any member may share the invitation.
        body = "The group gets a username of its own, and anyone with the link can join.",
        isSelected = selected == GroupVisibility.PUBLIC,
        enabled = enabled,
        onSelect = { onSelect(GroupVisibility.PUBLIC) },
    )

    if (errorText != null) {
        Spacer(Modifier.height(Space.one))
        Text(
            text = errorText,
            // The same `label.medium` in `danger` that `SwetTextField` gives its own supporting
            // text, so an error under a card and an error under a box read as the same thing.
            style = MaterialTheme.typography.labelMedium,
            color = colors.danger,
            modifier = Modifier.padding(horizontal = Space.two),
        )
    }
}

/** The selected mark's outer ring. 16dp is one step of §6.1's scale, not a picked number. */
private val ChoiceMarkSize: Dp = Space.two

/** Its filled centre, one half-step in from the ring. */
private val ChoiceMarkDotSize: Dp = Space.one

@Composable
private fun VisibilityOption(
    title: String,
    body: String,
    isSelected: Boolean,
    enabled: Boolean,
    onSelect: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val shape = RoundedCornerShape(Radius.md)

    Row(
        modifier = Modifier
            .fillMaxWidth()
            // §6.3's minimum, which two lines of text happen to clear -- but the rule is stated
            // rather than relied on, because a translation could be shorter. `defaultMinSize`
            // rather than a fixed height, so a large system font grows the card instead of
            // clipping the sentence inside it.
            .defaultMinSize(minHeight = TouchTarget.minimum)
            // Clipped before the press, so the state layer stops at the corners.
            .clip(shape)
            .background(colors.card)
            .border(
                // §8.2's subtle border by default; doubled and in the accent when selected. The
                // border is one of TWO signals -- see the mark below -- because §22.2 does not
                // allow a state to be carried by colour alone.
                width = if (isSelected) Sizes.borderHairline * 2 else Sizes.borderHairline,
                color = if (isSelected) colors.accent else colors.border,
                shape = shape,
            )
            .swetPressable(
                onClick = onSelect,
                enabled = enabled,
                // Announced as a radio button, so a screen reader says "selected" / "not
                // selected" rather than reading two unrelated buttons.
                role = Role.RadioButton,
                // A card is a surface: §25.1's state layer, not a scale. A full-width card
                // shrinking moves both its edges, which reads as the page twitching.
                feedback = SwetPressFeedback.StateLayer,
            )
            .semantics { selected = isSelected }
            .padding(Space.two),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f)) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                color = if (enabled) colors.textPrimary else colors.textTertiary,
            )
            Spacer(Modifier.height(Space.half))
            Text(
                text = body,
                style = MaterialTheme.typography.bodySmall,
                color = colors.textSecondary,
            )
        }

        Spacer(Modifier.width(Space.two))

        ChoiceMark(isSelected = isSelected)
    }
}

/**
 * The second signal, so the selection is not carried by the border colour alone (§22.2).
 *
 * Decorative in the accessibility tree: the row already announces itself as a selected radio
 * button, and a description here would have a screen reader say it twice.
 */
@Composable
private fun ChoiceMark(isSelected: Boolean) {
    val colors = SwetgramThemeTokens.colors

    Box(
        modifier = Modifier
            .size(ChoiceMarkSize)
            .border(
                width = Sizes.borderHairline * 2,
                color = if (isSelected) colors.accent else colors.borderStrong,
                shape = CircleShape,
            ),
        contentAlignment = Alignment.Center,
    ) {
        if (isSelected) {
            Box(
                Modifier
                    .size(ChoiceMarkDotSize)
                    .clip(CircleShape)
                    .background(colors.accent),
            )
        }
    }
}

// ---------------------------------------------------------------------------------------------
// Notes and copy
// ---------------------------------------------------------------------------------------------

/** A quiet line under a field: what it is for, or why it cannot be edited. */
@Composable
private fun FieldNote(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = SwetgramThemeTokens.colors.textSecondary,
        modifier = Modifier.padding(horizontal = Space.two, vertical = Space.half),
    )
}

/**
 * What to show under a field: the SERVER's verdict if there is one, otherwise the client's own --
 * and the client's only once Create has been pressed.
 *
 * The server wins because it knows things this device cannot: that a username is taken, that the
 * account's email is not verified yet. The client's is suppressed before the first submit because
 * a form that turns red while somebody is still typing is accusing them of a mistake they have not
 * finished not making.
 */
private fun fieldMessage(
    state: CreateGroupUiState,
    field: String,
    problem: CreateGroupProblem?,
): String? = state.fieldMessages[field]
    ?: problem?.takeIf { state.submitted }?.let { createGroupProblemText(it) }

/**
 * SDS §18.2: say what is wrong and what to do about it. Never "invalid".
 *
 * THE USERNAME SENTENCES SAY NOTHING ABOUT PEOPLE. Public-group usernames are a SEPARATE namespace
 * from user usernames -- DECISIONS §7 states it and states the accepted cost, that "`@alex` the
 * person and `@alex` the group can coexist" -- so the registration form's "That username is not
 * available" is the wrong sentence here twice over: it is about a different namespace, and a user
 * who believed it would go looking for an account that has nothing to do with the refusal.
 *
 * The bounds are read from `Constraints`, the same object the wire contract exposes and the same
 * one `validateUsername` checks against, so the number in the sentence cannot drift from the
 * number in the rule.
 */
// TODO(localization): these strings move into resources for EN + RU.
internal fun createGroupProblemText(problem: CreateGroupProblem): String = when (problem) {
    CreateGroupProblem.NameRequired -> "Give the group a name."
    CreateGroupProblem.NameTooLong ->
        "That name is too long. $GROUP_NAME_MAX characters at most."
    CreateGroupProblem.DescriptionTooLong ->
        "That description is too long. $GROUP_DESCRIPTION_MAX characters at most."
    CreateGroupProblem.VisibilityNotChosen ->
        "Choose who can join. This cannot be changed later."
    CreateGroupProblem.UsernameRequired -> "A public group needs a username."
    CreateGroupProblem.UsernameTooShort ->
        "Group usernames are at least ${Constraints.USERNAME_MIN} characters."
    CreateGroupProblem.UsernameTooLong ->
        "Group usernames are at most ${Constraints.USERNAME_MAX} characters."
    CreateGroupProblem.UsernameBadCharacters ->
        "Use lowercase letters, numbers and underscores only."
    CreateGroupProblem.UsernameReserved -> "That username is reserved and cannot be used."
}
